import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { X } from "lucide-react";
import StatusDropdown from "../../Dropdowns/StatusDropdown";
import Dropdown from "../../Dropdowns/Dropdown";
import { useUserProfile } from "../../../fetches/GetUserProfileFetch";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useDropdownData from "../../../fetches/loadDropDownData";
import MotivationDropdown from "../../Dropdowns/MotivationDropdown";
import InformationTabContent from "./InformationTablContent";
import MotivationTabContent from "./MotivationTabContent";
import ParametersTabContent from "./ParametersTabContent";
import DatesTabContent from "./DatesTabContent";
import PositionTabContent from "./PositionTabContent";

// Define parameter groups outside component to prevent recreation on each render
const parameterGroups = [
  { group: "P01", fields: ["0", "1", "2", "3", "4"] },
  { group: "P02", fields: ["0", "1"] },
  { group: "P03", fields: ["0", "1"] },
  { group: "P04", fields: ["0", "1"] },
  { group: "P05", fields: ["0", "1"] },
  { group: "P06", fields: ["0", "1"] },
  { group: "P07", fields: ["0", "1"] },
  { group: "P08", fields: ["0", "1"] },
  { group: "P09", fields: ["0", "1"] },
  { group: "P10", fields: ["0", "1"] },
  { group: "P11", fields: ["0", "1"] },
];

// Helper function to extract parameter values from initialData
function getParameterValues(data) {
  const values = {};

  parameterGroups.forEach(({ group, fields }) => {
    const divisorKey = `${group}divisor`;
    values[divisorKey] = data[divisorKey] || 0;

    fields.forEach((field) => {
      const fieldKey = `${group}_${field}`;
      values[fieldKey] = data[fieldKey] || 0;
    });
  });

  return values;
}

const RecruitModalBase = ({
  isOpen,
  onClose,
  modalTitle = "Работа с кандидатом",
  initialData = {},
  onSubmit,
  submitButtonText = "Сохранить",
  isEditMode = false,
}) => {
  const [showStatus, setShowStatus] = useState(false);
  // Store initialData in a ref to prevent re-renders
  const initialDataRef = useRef(initialData);
  const initialRenderRef = useRef(true);

  // Only log on first render
  if (initialRenderRef.current) {
    console.log("RecruitModalBase received initialData:", initialData);
    initialRenderRef.current = false;
  }

  const {
    dropdownData: rawDropdownData,
    loading: dataLoading,
    error: dataError,
  } = useDropdownData();
  const [activeTab, setActiveTab] = useState("information");
  const [entityType, setEntityType] = useState("candidate");

  // Use a single state object for parsed data to prevent multiple re-renders
  const [parsedData, setParsedData] = useState({
    companies: [],
    teams: [],
    departments: [],
    divisions: [],
    positions: [],
  });

  // Refs for form data - won't cause re-renders when updated
  const formRefs = useRef({
    // Basic info - match field names with your data structure
    first_name: initialData.first_name || "",
    last_name: initialData.last_name || "",
    phone_number: initialData.phone_number || "",
    email: initialData.email || "",
    linkedin: initialData.linkedin || "",
    FB_IG: initialData.FB_IG || "",
    NDA_date: initialData.NDA_date || "No",
    status: initialData.status || "A",

    // Company info - these will be objects with id and name
    company: initialData.company || "",
    team: initialData.team || "",
    department: initialData.department || "",
    division: initialData.division || "",
    position: initialData.position || "",

    // Dates
    date_of_interview: initialData.date_of_interview || "",
    date_of_translate: initialData.date_of_translate || "",
    date_of_offer: initialData.date_of_offer || "",
    date_of_fired: initialData.date_of_fired || "",
    working_phone: initialData.working_phone || "",
    commentary: initialData.commentary || "",

    // Parameters
    ...getParameterValues(initialData),

    // Motivation
    motivation1: initialData.motivation1 || "",
    motivation2: initialData.motivation2 || "",
    motivation3: initialData.motivation3 || "",
    motivation4: initialData.motivation4 || "",
  });

  // State for dropdowns - initialize only once with initialData
  const [status, setStatus] = useState(initialData.status || "A");
  const [company, setCompany] = useState(initialData.company || "");
  const [team, setTeam] = useState(initialData.team || "");
  const [department, setDepartment] = useState(initialData.department || "");
  const [division, setDivision] = useState(initialData.division || "");
  const [position, setPosition] = useState(initialData.position || "");
  const [ndaDate, setNdaDate] = useState(initialData.NDA_date || "No");
  const [motivation, setMotivation] = useState({
    motivation1: initialData.motivation1 || "",
    motivation2: initialData.motivation2 || "",
    motivation3: initialData.motivation3 || "",
    motivation4: initialData.motivation4 || "",
  });

  // For parameters, create state to properly populate form fields
  const [parameterValues, setParameterValues] = useState(
    getParameterValues(initialData)
  );

  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);

  // Track if dropdown data is already processed
  const isDataProcessed = useRef(false);

  // Track if initialData has already been processed
  const initialDataProcessed = useRef(false);

  // Get user profile and determine role-based flag
  const { roleType, getUserProfile } = useUserProfile();
  // Initialize component
  useEffect(() => {
    getUserProfile();

    // Set the mounted flag
    isMounted.current = true;

    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, [getUserProfile]);

  // CRITICAL FIX: Update state when initialData changes, but ONLY ONCE
  // and only if initialData has actual content
  useEffect(() => {
    if (!isMounted.current || initialDataProcessed.current) return;

    // Only process non-empty initialData
    if (initialData && Object.keys(initialData).length > 0) {
      console.log("Processing initialData once:", initialData);
      initialDataProcessed.current = true;

      // Store initialData in ref
      initialDataRef.current = initialData;

      // Update formRefs with the correct field names
      formRefs.current = {
        // Basic info
        first_name: initialData.first_name || "",
        last_name: initialData.last_name || "",
        phone_number: initialData.phone_number || "",
        email: initialData.email || "",
        linkedin: initialData.linkedin || "",
        FB_IG: initialData.FB_IG || "",
        NDA_date: initialData.NDA_date || "No",
        status: initialData.status || "A",

        // Company info
        company: initialData.company || "",
        team: initialData.team || "",
        department: initialData.department || "",
        division: initialData.division || "",
        position: initialData.position || "",

        // Dates
        date_of_interview: initialData.date_of_interview || "",
        date_of_translate: initialData.date_of_translate || "",
        date_of_offer: initialData.date_of_offer || "",
        date_of_fired: initialData.date_of_fired || "",
        working_phone: initialData.working_phone || "",
        commentary: initialData.commentary || "",

        // Parameter values and motivation will be handled separately
        ...getParameterValues(initialData),

        // Motivation
        motivation1: initialData.motivation1 || "",
        motivation2: initialData.motivation2 || "",
        motivation3: initialData.motivation3 || "",
        motivation4: initialData.motivation4 || "",
      };

      // Update form state only if the values are different from current state
      setStatus((prevStatus) => initialData.status || prevStatus || "A");
      setNdaDate((prevNda) => initialData.NDA_date || prevNda || "No");

      // Deliberately NOT updating other state here to avoid re-renders
      // They will be updated when dropdown data is loaded
    }
  }, []); // Empty dependency array - run only once

  // Parse dropdown data and match with initialData when available
  useEffect(() => {
    if (
      !rawDropdownData ||
      Object.keys(rawDropdownData).length === 0 ||
      isDataProcessed.current
    )
      return;

    // Get initialData from ref to avoid re-renders
    const initialDataValue = initialDataRef.current;

    const parseDropdownData = (data) => {
      if (!data)
        return {
          companies: [],
          teams: [],
          departments: [],
          divisions: [],
          positions: [],
        };

      const companies = [];
      const teams = [];
      const departments = [];
      const divisions = [];
      const positions = [];

      // Process companies
      Object.entries(data).forEach(([name, company]) => {
        companies.push({ id: company.id, name });

        // Process teams
        if (company.teams) {
          company.teams.forEach((team) => {
            teams.push(team);
          });
        }

        // Process departments and their divisions/positions
        if (company.departments) {
          company.departments.forEach((dept) => {
            departments.push({ id: dept.id, name: dept.name });

            // Add divisions
            if (dept.divisions) {
              dept.divisions.forEach((div) => {
                divisions.push({ id: div.id, name: div.name });

                // Add positions
                if (div.positions) {
                  div.positions.forEach((pos) => {
                    positions.push(pos);
                  });
                }
              });
            }
          });
        }
      });

      return { companies, teams, departments, divisions, positions };
    };

    // Parse data once and store it
    const parsed = parseDropdownData(rawDropdownData);
    console.log("Parsed dropdown data:", parsed);

    if (isMounted.current) {
      setParsedData(parsed);
      isDataProcessed.current = true;

      // Match dropdown items with initialData - but only do this once
      if (initialDataValue && Object.keys(initialDataValue).length > 0) {
        // For companies and other entities, match by name
        if (initialDataValue.company) {
          const foundCompany = parsed.companies.find(
            (c) => c.name === initialDataValue.company
          );
          if (foundCompany) setCompany(foundCompany);
        }

        if (initialDataValue.team) {
          const foundTeam = parsed.teams.find(
            (t) => t.name === initialDataValue.team
          );
          if (foundTeam) setTeam(foundTeam);
        }

        if (initialDataValue.department) {
          const foundDepartment = parsed.departments.find(
            (d) => d.name === initialDataValue.department
          );
          if (foundDepartment) setDepartment(foundDepartment);
        }

        if (initialDataValue.division) {
          const foundDivision = parsed.divisions.find(
            (d) => d.name === initialDataValue.division
          );
          if (foundDivision) setDivision(foundDivision);
        }

        if (initialDataValue.position) {
          const foundPosition = parsed.positions.find(
            (p) => p.name === initialDataValue.position
          );
          if (foundPosition) setPosition(foundPosition);
        }
      }
    }
  }, [rawDropdownData]); // Only depend on rawDropdownData

  // Memoize computed values
  const isAllowToChangeSystemComponent = useMemo(
    () => !(roleType === 3),
    [roleType]
  );

  // Memoize handlers to prevent recreation on every render
  const handleStatusChange = useCallback((statusCode) => {
    setStatus(statusCode);
    formRefs.current.status = statusCode;
  }, []);

  const handleReset = useCallback(() => {
    // Reset all input fields
    const inputFields = document.querySelectorAll("input, textarea");
    inputFields.forEach((field) => {
      if (field.name) {
        field.value = "";
      }
    });

    // Reset dropdown states
    setStatus("A");
    setCompany("");
    setTeam("");
    setDepartment("");
    setDivision("");
    setPosition("");
    setNdaDate("No");

    // Reset parameter values
    setParameterValues({});

    // Reset motivation
    setMotivation({
      motivation1: "",
      motivation2: "",
      motivation3: "",
      motivation4: "",
    });

    // Reset the form refs
    formRefs.current = {
      first_name: "",
      last_name: "",
      phone_number: "",
      email: "",
      linkedin: "",
      FB_IG: "",
      NDA_date: "No",
      status: "A",
      company: "",
      team: "",
      department: "",
      division: "",
      position: "",
      date_of_interview: "",
      date_of_translate: "",
      date_of_offer: "",
      date_of_fired: "",
      working_phone: "",
      commentary: "",
    };
  }, []);

  const collectFormData = useCallback(() => {
    // Collect all input values
    const inputFields = document.querySelectorAll("input, textarea, select");

    // First collect the basic form fields
    inputFields.forEach((field) => {
      if (field.name && field.name !== "") {
        formRefs.current[field.name] = field.value;
      }
    });

    // Add dropdown values
    formRefs.current.status = status;
    formRefs.current.company = company;
    formRefs.current.team = team;
    formRefs.current.department = department;
    formRefs.current.division = division;
    formRefs.current.position = position;
    formRefs.current.NDA_date = ndaDate;

    // Add motivation values
    formRefs.current.motivation1 = motivation.motivation1;
    formRefs.current.motivation2 = motivation.motivation2;
    formRefs.current.motivation3 = motivation.motivation3;
    formRefs.current.motivation4 = motivation.motivation4;

    // Convert numeric fields for parameters
    parameterGroups.forEach(({ group, fields }) => {
      const divisorKey = `${group}divisor`;
      const divisorElement = document.querySelector(`[name="${divisorKey}"]`);
      formRefs.current[divisorKey] =
        divisorElement && divisorElement.value !== ""
          ? Number(divisorElement.value)
          : 0;

      fields.forEach((field) => {
        const fieldKey = `${group}_${field}`;
        const fieldElement = document.querySelector(`[name="${fieldKey}"]`);
        formRefs.current[fieldKey] =
          fieldElement && fieldElement.value !== ""
            ? Number(fieldElement.value)
            : 0;
      });
    });

    return formRefs.current;
  }, [
    status,
    company,
    team,
    department,
    division,
    position,
    ndaDate,
    motivation,
  ]);

  const validateForm = useCallback((formData) => {
    const errors = [];

    // Validate required fields
    if (!formData.first_name) {
      errors.push("Имя");
    }

    if (!formData.last_name) {
      errors.push("Фамилия");
    }

    if (!formData.company) {
      errors.push("Компания");
    }

    if (errors.length > 0) {
      alert(`Пожалуйста, заполните обязательные поля: ${errors.join(", ")}`);
      return false;
    }

    // Check each parameter group: the sum of non-divisor fields must equal the divisor.
    for (const { group, fields } of parameterGroups) {
      const divisorKey = `${group}divisor`;
      const divisorValue = Number(formData[divisorKey]) || 0;

      if (divisorValue > 0) {
        const total = fields.reduce((acc, field) => {
          const key = `${group}_${field}`;
          return acc + (Number(formData[key]) || 0);
        }, 0);

        if (total !== divisorValue) {
          alert(
            `Для группы ${group}: сумма (${total}) не равна делителю (${divisorValue}).`
          );
          return false;
        }
      }
    }
    return true;
  }, []);

  // This function prevents default form submission
  const preventFormSubmission = useCallback((e) => {
    if (e) e.preventDefault();
    return false;
  }, []);

  // Memoize the submit handler
  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      // Collect form data from refs and DOM
      const formData = collectFormData();
      console.log("Form data collected:", formData);

      // Validate the form
      if (!validateForm(formData)) {
        return;
      }

      // Map formData to the format expected by backend - using the same field names
      const serverData = {
        ...(isEditMode &&
          initialDataRef.current.id && { id: initialDataRef.current.id }), // Include ID in edit mode
        rating: formData.rating || 0,
        first_name: formData.first_name,
        status: formData.status,
        last_name: formData.last_name,
        company:
          typeof formData.company === "object"
            ? formData.company.name
            : formData.company,
        team:
          typeof formData.team === "object"
            ? formData.team.name
            : formData.team,
        department:
          typeof formData.department === "object"
            ? formData.department.name
            : formData.department,
        division:
          typeof formData.division === "object"
            ? formData.division.name
            : formData.division,
        position:
          typeof formData.position === "object"
            ? formData.position.name
            : formData.position,
        date_of_interview: formData.date_of_interview || null,
        date_of_translate: formData.date_of_translate || null,
        commentary: formData.commentary,
        date_of_offer: formData.date_of_offer || null,
        date_of_fired: formData.date_of_fired || null,
        working_phone: formData.working_phone,
        NDA_date: formData.NDA_date,
        FB_IG: formData.FB_IG,
        linkedin: formData.linkedin,
        phone_number: formData.phone_number,
        email: formData.email,
        // Parameters
        P01divisor: formData.P01divisor,
        P01_0: formData.P01_0,
        P01_1: formData.P01_1,
        P01_2: formData.P01_2,
        P01_3: formData.P01_3,
        P01_4: formData.P01_4,
        P02divisor: formData.P02divisor,
        P02_0: formData.P02_0,
        P02_1: formData.P02_1,
        P03divisor: formData.P03divisor,
        P03_0: formData.P03_0,
        P03_1: formData.P03_1,
        P04divisor: formData.P04divisor,
        P04_0: formData.P04_0,
        P04_1: formData.P04_1,
        P05divisor: formData.P05divisor,
        P05_0: formData.P05_0,
        P05_1: formData.P05_1,
        P06divisor: formData.P06divisor,
        P06_0: formData.P06_0,
        P06_1: formData.P06_1,
        P07divisor: formData.P07divisor,
        P07_0: formData.P07_0,
        P07_1: formData.P07_1,
        P08divisor: formData.P08divisor,
        P08_0: formData.P08_0,
        P08_1: formData.P08_1,
        P09divisor: formData.P09divisor,
        P09_0: formData.P09_0,
        P09_1: formData.P09_1,
        P10divisor: formData.P10divisor,
        P10_0: formData.P10_0,
        P10_1: formData.P10_1,
        P11divisor: formData.P11divisor,
        P11_0: formData.P11_0,
        P11_1: formData.P11_1,
        motivation1: formData.motivation1,
        motivation2: formData.motivation2,
        motivation3: formData.motivation3,
        motivation4: formData.motivation4,
      };

      console.log("Submitting server data:", serverData);

      try {
        await onSubmit(serverData);
        const successMessage = isEditMode
          ? "Кандидат успешно обновлен"
          : "Кандидат успешно добавлен";
        toast.success(successMessage);
        onClose();
        window.location.reload();
      } catch (error) {
        toast.error(`Не удалось сохранить запись: ${error.message}`);
        console.error("Save error:", error);
      }
    },
    [collectFormData, validateForm, isEditMode, onSubmit, onClose]
  );

  // Prevent the dropdown click from triggering form submission
  const handleDropdownClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }, []);
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10); // Changed from slice(0, 16) to slice(0, 10)
  };

  const InformationTab = useMemo(() => {
    return () => (
      <InformationTabContent
        initialData={initialDataRef.current}
        status={status}
        handleStatusChange={handleStatusChange}
        ndaDate={ndaDate}
        setNdaDate={setNdaDate}
        showStatus={showStatus}
        setShowStatus={setShowStatus}
      />
    );
  }, [
    status,
    handleStatusChange,
    ndaDate,
    setNdaDate,
    showStatus,
    setShowStatus,
  ]);

  const PositionTab = useMemo(() => {
    return () => (
      <PositionTabContent
        dataLoading={dataLoading}
        dataError={dataError}
        parsedData={parsedData}
        company={company}
        setCompany={setCompany}
        team={team}
        setTeam={setTeam}
        department={department}
        setDepartment={setDepartment}
        division={division}
        setDivision={setDivision}
        position={position}
        setPosition={setPosition}
        handleDropdownClick={handleDropdownClick}
      />
    );
  }, [
    dataLoading,
    dataError,
    parsedData,
    company,
    team,
    department,
    division,
    position,
    handleDropdownClick,
    setCompany,
    setTeam,
    setDepartment,
    setDivision,
    setPosition,
  ]);

  const DatesTab = useMemo(() => {
    return () => (
      <DatesTabContent
        initialData={initialDataRef.current}
        formatDateForInput={formatDateForInput}
      />
    );
  }, [formatDateForInput]);

  const ParametersTab = useMemo(() => {
    return () => (
      <ParametersTabContent
        initialData={initialDataRef.current}
        isAllowToChangeSystemComponent={isAllowToChangeSystemComponent}
        parameterGroups={parameterGroups}
      />
    );
  }, [isAllowToChangeSystemComponent]);

  const MotivationTab = useMemo(() => {
    return () => (
      <MotivationTabContent
        initialData={initialDataRef.current}
        setMotivation={setMotivation}
      />
    );
  }, [setMotivation]);

  // Render the appropriate tab content based on activeTab state
  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case "information":
        return <InformationTab />;
      case "position":
        return <PositionTab />;
      case "dates":
        return <DatesTab />;
      case "parameters":
        return <ParametersTab />;
      case "motivation":
        return <MotivationTab />;
      default:
        return <InformationTab />; // Default to information tab
    }
  }, [
    activeTab,
    InformationTab,
    PositionTab,
    DatesTab,
    ParametersTab,
    MotivationTab,
  ]); // Add memoized tabs to dependency array

  // Don't render the modal if it's not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div
        className="bg-white rounded-md flex flex-col relative"
        style={{ width: "980px", height: "620px" }}
      >
        {/* Header */}
        <div
          className="bg-[#E2E7F3] rounded-t-md h-24 flex items-center justify-center mb-4 -mx-0 -mt-0 px-6"
          style={{ backgroundColor: "#0832DE" }}
        >
          <h2 className="text-white text-xl font-bold">{modalTitle}</h2>
        </div>

        {/* Content area with padding */}
        <div className="flex-1 flex flex-col px-6 pb-6 overflow-hidden">
          {/* Entity Type Selection */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex space-x-2">
              {/* Entity Type Buttons - Exact SVG reproductions */}
              <button type="button" onClick={() => setEntityType("candidate")}>
                <svg
                  width="91"
                  height="50"
                  viewBox="0 0 91 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 25C0 11.1929 11.1929 0 25 0H91V50H25C11.1929 50 0 38.8071 0 25Z"
                    fill={entityType === "candidate" ? "#0832DE" : "#E2E7F3"}
                  />
                  <path
                    d="M26.4204 21.1022C26.6009 21.0353 26.796 20.9987 26.9997 20.9987C27.9202 20.9987 28.6664 21.7449 28.6664 22.6654C28.6664 23.5859 27.9202 24.3321 26.9997 24.3321C26.796 24.3321 26.6009 24.2955 26.4204 24.2286M18 28.9987C18.4609 27.4606 19.6467 26.9987 22.3333 26.9987C25.02 26.9987 26.2057 27.4606 26.6667 28.9987M27.3335 26.332C28.9355 26.3954 29.6863 26.7576 30.0002 27.6654M24.6667 22.6654C24.6667 23.954 23.622 24.9987 22.3333 24.9987C21.0447 24.9987 20 23.954 20 22.6654C20 21.3767 21.0447 20.332 22.3333 20.332C23.622 20.332 24.6667 21.3767 24.6667 22.6654Z"
                    stroke={entityType === "candidate" ? "white" : "#2B3674"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M38.152 30V18.8H42.216C45.704 18.8 48.088 21.088 48.088 24.4C48.088 27.632 45.624 30 42.216 30H38.152ZM40.232 28.032H42.216C44.536 28.032 45.944 26.688 45.944 24.4C45.944 22.112 44.536 20.752 42.216 20.752H40.232V28.032ZM52.8894 30.16C50.6654 30.16 48.9534 28.352 48.9534 26.016C48.9534 23.68 50.6814 21.84 52.8894 21.84C53.8654 21.84 54.7454 22.208 55.4014 22.832V22H57.3534V30H55.4014V29.184C54.7454 29.792 53.8814 30.16 52.8894 30.16ZM51.0174 26C51.0174 27.36 51.9614 28.352 53.2574 28.352C54.5534 28.352 55.5134 27.36 55.5134 26C55.5134 24.64 54.5534 23.648 53.2574 23.648C51.9614 23.648 51.0174 24.64 51.0174 26ZM58.3865 22H60.2105V19.92H62.1145V22H64.8985V23.728H62.1145V27.616C62.1145 28.016 62.3225 28.24 62.7065 28.24H64.5785V30H62.3225C61.0905 30 60.1625 29.344 60.1625 27.824V23.728H58.3865V22ZM69.2706 30.16C67.0466 30.16 65.3346 28.352 65.3346 26.016C65.3346 23.68 67.0626 21.84 69.2706 21.84C70.2466 21.84 71.1266 22.208 71.7826 22.832V22H73.7346V30H71.7826V29.184C71.1266 29.792 70.2626 30.16 69.2706 30.16ZM67.3986 26C67.3986 27.36 68.3426 28.352 69.6386 28.352C70.9346 28.352 71.8946 27.36 71.8946 26C71.8946 24.64 70.9346 23.648 69.6386 23.648C68.3426 23.648 67.3986 24.64 67.3986 26Z"
                    fill={entityType === "candidate" ? "white" : "#2B3674"}
                  />
                </svg>
              </button>

              <button type="button" onClick={() => setEntityType("skill")}>
                <svg
                  width="91"
                  height="50"
                  viewBox="0 0 91 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="91"
                    height="50"
                    fill={entityType === "skill" ? "#0832DE" : "#E2E7F3"}
                  />
                  <path
                    d="M20.75 24C21.9926 24 23 22.9926 23 21.75C23 20.5074 21.9926 19.5 20.75 19.5C19.5074 19.5 18.5 20.5074 18.5 21.75C18.5 22.9926 19.5074 24 20.75 24Z"
                    stroke={entityType === "skill" ? "white" : "#2B3674"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M27.25 24C28.4926 24 29.5 22.9926 29.5 21.75C29.5 20.5074 28.4926 19.5 27.25 19.5C26.0074 19.5 25 20.5074 25 21.75C25 22.9926 26.0074 24 27.25 24Z"
                    stroke={entityType === "skill" ? "white" : "#2B3674"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.75 30.5C21.9926 30.5 23 29.4926 23 28.25C23 27.0074 21.9926 26 20.75 26C19.5074 26 18.5 27.0074 18.5 28.25C18.5 29.4926 19.5074 30.5 20.75 30.5Z"
                    stroke={entityType === "skill" ? "white" : "#2B3674"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M27.25 26.5V30"
                    stroke={entityType === "skill" ? "white" : "#2B3674"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M29 28.25H25.5"
                    stroke={entityType === "skill" ? "white" : "#2B3674"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M39.08 26.56C39.432 27.648 40.296 28.288 41.704 28.288C42.856 28.288 43.752 27.92 43.752 26.96C43.752 26.112 43.304 25.744 41.368 25.28C38.616 24.624 37.672 23.632 37.672 21.84C37.672 20 39.4 18.624 41.528 18.624C43.624 18.624 45.064 19.584 45.624 21.152L43.784 21.904C43.464 21.024 42.616 20.496 41.576 20.496C40.44 20.496 39.752 20.928 39.752 21.872C39.752 22.64 40.168 22.976 41.976 23.376C44.68 24 45.832 24.976 45.832 26.8C45.832 28.8 44.088 30.16 41.72 30.16C39.512 30.16 37.864 29.104 37.304 27.376L39.08 26.56ZM47.2611 30V18.8H49.2131V25.328L52.3811 22H54.7491L51.3731 25.472L54.8291 30H52.4451L50.0451 26.8L49.1811 27.632V30H47.2611ZM55.8301 22H57.7981V30H55.8301V22ZM55.5901 19.696C55.5901 18.976 56.1181 18.448 56.8221 18.448C57.5261 18.448 58.0541 18.976 58.0541 19.696C58.0541 20.4 57.5261 20.928 56.8221 20.928C56.1181 20.928 55.5901 20.4 55.5901 19.696ZM60.0364 18.8H62.0044V30H60.0364V18.8ZM64.2426 18.8H66.2106V30H64.2426V18.8ZM69.3643 27.44C69.5563 28.128 70.1003 28.464 71.0763 28.464C72.1803 28.464 72.5483 28.144 72.5483 27.68C72.5483 27.136 72.0683 27.088 70.9163 26.848C69.4283 26.544 67.6842 26.128 67.6842 24.272C67.6842 22.88 69.0763 21.84 70.9643 21.84C72.7083 21.84 73.9243 22.544 74.3723 23.744L72.5323 24.4C72.3083 23.824 71.7643 23.552 70.9323 23.552C70.1003 23.552 69.6363 23.808 69.6363 24.288C69.6363 24.784 70.2123 24.96 71.3643 25.216C72.8523 25.536 74.5163 25.952 74.5163 27.632C74.5163 29.152 73.0123 30.16 70.9643 30.16C69.2203 30.16 67.9243 29.392 67.4763 28.064L69.3643 27.44Z"
                    fill={entityType === "skill" ? "white" : "#2B3674"}
                  />
                </svg>
              </button>

              <button type="button" onClick={() => setEntityType("dictionary")}>
                <svg
                  width="126"
                  height="50"
                  viewBox="0 0 126 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0H101C114.807 0 126 11.1929 126 25C126 38.8071 114.807 50 101 50H0V0Z"
                    fill={entityType === "dictionary" ? "#0832DE" : "#E2E7F3"}
                  />
                  <path
                    d="M24.5346 26.6364H28.8013C28.9427 26.6364 29.0784 26.5789 29.1784 26.4766C29.2784 26.3743 29.3346 26.2356 29.3346 26.0909V23.9091C29.3346 23.7644 29.2784 23.6257 29.1784 23.5234C29.0784 23.4211 28.9427 23.3636 28.8013 23.3636H24.5346C24.3932 23.3636 24.2575 23.4211 24.1575 23.5234C24.0575 23.6257 24.0013 23.7644 24.0013 23.9091V24.4545H21.868V22.2727H23.468C23.6094 22.2727 23.7451 22.2153 23.8451 22.113C23.9451 22.0107 24.0013 21.8719 24.0013 21.7273V19.5455C24.0013 19.4008 23.9451 19.2621 23.8451 19.1598C23.7451 19.0575 23.6094 19 23.468 19H19.2013C19.0599 19 18.9242 19.0575 18.8242 19.1598C18.7242 19.2621 18.668 19.4008 18.668 19.5455V21.7273C18.668 21.8719 18.7242 22.0107 18.8242 22.113C18.9242 22.2153 19.0599 22.2727 19.2013 22.2727H20.8013V29.3636C20.8013 29.5083 20.8575 29.647 20.9575 29.7493C21.0575 29.8516 21.1932 29.9091 21.3346 29.9091H24.0013V30.4545C24.0013 30.5992 24.0575 30.7379 24.1575 30.8402C24.2575 30.9425 24.3932 31 24.5346 31H28.8013C28.9427 31 29.0784 30.9425 29.1784 30.8402C29.2784 30.7379 29.3346 30.5992 29.3346 30.4545V28.2727C29.3346 28.1281 29.2784 27.9893 29.1784 27.887C29.0784 27.7847 28.9427 27.7273 28.8013 27.7273H24.5346C24.3932 27.7273 24.2575 27.7847 24.1575 27.887C24.0575 27.9893 24.0013 28.1281 24.0013 28.2727V28.8182H21.868V25.5455H24.0013V26.0909C24.0013 26.2356 24.0575 26.3743 24.1575 26.4766C24.2575 26.5789 24.3932 26.6364 24.5346 26.6364ZM25.068 24.4545H28.268V25.5455H25.068V24.4545ZM19.7346 20.0909H22.9346V21.1818H19.7346V20.0909ZM25.068 28.8182H28.268V29.9091H25.068V28.8182Z"
                    fill={entityType === "dictionary" ? "white" : "#2B3674"}
                  />
                  <path
                    d="M39.08 26.56C39.432 27.648 40.296 28.288 41.704 28.288C42.856 28.288 43.752 27.92 43.752 26.96C43.752 26.112 43.304 25.744 41.368 25.28C38.616 24.624 37.672 23.632 37.672 21.84C37.672 20 39.4 18.624 41.528 18.624C43.624 18.624 45.064 19.584 45.624 21.152L43.784 21.904C43.464 21.024 42.616 20.496 41.576 20.496C40.44 20.496 39.752 20.928 39.752 21.872C39.752 22.64 40.168 22.976 41.976 23.376C44.68 24 45.832 24.976 45.832 26.8C45.832 28.8 44.088 30.16 41.72 30.16C39.512 30.16 37.864 29.104 37.304 27.376L39.08 26.56ZM46.4771 22H48.3011V19.92H50.2051V22H52.9891V23.728H50.2051V27.616C50.2051 28.016 50.4131 28.24 50.7971 28.24H52.6691V30H50.4131C49.1811 30 48.2531 29.344 48.2531 27.824V23.728H46.4771V22ZM54.2018 30V22H56.1538V23.008C56.6178 22.368 57.2418 22 57.9938 22H59.5298V23.76H57.9138C56.8098 23.76 56.1538 24.448 56.1538 25.776V30H54.2018ZM62.4682 26.432C62.4682 27.728 63.0282 28.368 64.1482 28.368C65.3162 28.368 66.0522 27.616 66.1483 26.352L66.1642 22H68.1163V30H66.1642V29.136C65.5563 29.776 64.7242 30.16 63.7962 30.16C61.6842 30.16 60.4682 28.896 60.4682 26.672V22H62.4682V26.432ZM69.4628 26C69.4628 23.584 71.2388 21.856 73.7188 21.84C75.4468 21.856 76.7108 22.576 77.3348 23.84L75.6068 24.816C75.2708 24.096 74.5828 23.664 73.6868 23.664C72.4068 23.664 71.5268 24.624 71.5268 26C71.5268 27.376 72.4068 28.336 73.6868 28.336C74.5828 28.336 75.3028 27.936 75.6708 27.28L77.3348 28.16C76.6788 29.392 75.3348 30.16 73.6548 30.16C71.1108 30.16 69.4628 28.512 69.4628 26ZM77.6459 22H79.4699V19.92H81.3739V22H84.1579V23.728H81.3739V27.616C81.3739 28.016 81.5819 28.24 81.9659 28.24H83.8379V30H81.5819C80.3499 30 79.4219 29.344 79.4219 27.824V23.728H77.6459V22ZM87.2745 26.432C87.2745 27.728 87.8345 28.368 88.9545 28.368C90.1225 28.368 90.8585 27.616 90.9545 26.352L90.9705 22H92.9225V30H90.9705V29.136C90.3625 29.776 89.5305 30.16 88.6025 30.16C86.4905 30.16 85.2745 28.896 85.2745 26.672V22H87.2745V26.432ZM94.733 30V22H96.685V23.008C97.149 22.368 97.773 22 98.525 22H100.061V23.76H98.445C97.341 23.76 96.685 24.448 96.685 25.776V30H94.733ZM104.403 30.176C101.891 30.176 100.147 28.48 100.147 26.032C100.147 23.68 101.907 21.84 104.371 21.84C106.771 21.84 108.451 23.584 108.451 25.952C108.451 26.256 108.419 26.56 108.371 26.8H102.163C102.419 27.824 103.219 28.448 104.515 28.448C105.443 28.448 106.099 28.112 106.483 27.568L107.987 28.528C107.299 29.552 106.019 30.176 104.403 30.176ZM102.147 25.264H106.435C106.291 24.192 105.539 23.552 104.355 23.552C103.187 23.552 102.387 24.16 102.147 25.264Z"
                    fill={entityType === "dictionary" ? "white" : "#2B3674"}
                  />
                </svg>
              </button>
            </div>

            <div className="flex items-center">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2"
              >
                <path
                  d="M12 6C12.6628 6 13.2 6.53726 13.2 7.2V13.2C13.2 13.8628 12.6628 14.4 12 14.4C11.3372 14.4 10.8 13.8628 10.8 13.2V7.2C10.8 6.53726 11.3372 6 12 6Z"
                  fill="#5E6BA2"
                />
                <path
                  d="M12 18C12.6628 18 13.2 17.4628 13.2 16.8C13.2 16.1372 12.6628 15.6 12 15.6C11.3372 15.6 10.8 16.1372 10.8 16.8C10.8 17.4628 11.3372 18 12 18Z"
                  fill="#5E6BA2"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0ZM2.4 12C2.4 6.69806 6.69806 2.4 12 2.4C17.302 2.4 21.6 6.69806 21.6 12C21.6 17.302 17.302 21.6 12 21.6C6.69806 21.6 2.4 17.302 2.4 12Z"
                  fill="#5E6BA2"
                />
              </svg>
              {isEditMode ? (
                <span className="text-gray-700">
                  Редактирование существующей записи в таблице Аналитики.
                </span>
              ) : (
                <span className="text-gray-700">
                  Добавьте новую сущность в таблицу Аналитики.
                </span>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-between mb-4">
            <div className="flex">
              {/* Tab buttons with regular button components */}
              <button
                type="button"
                onClick={() => setActiveTab("information")}
                className={`w-36 h-10 rounded-md flex items-center justify-center font-medium ${
                  activeTab === "information"
                    ? "bg-[#0832DE] text-white"
                    : "bg-[#E2E7F3] text-gray-700"
                }`}
              >
                Информация
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("position")}
                className={`w-36 h-10 rounded-md flex items-center justify-center font-medium ml-2 ${
                  activeTab === "position"
                    ? "bg-[#0832DE] text-white"
                    : "bg-[#E2E7F3] text-gray-700"
                }`}
              >
                Должность
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("dates")}
                className={`w-36 h-10 rounded-md flex items-center justify-center font-medium ml-2 ${
                  activeTab === "dates"
                    ? "bg-[#0832DE] text-white"
                    : "bg-[#E2E7F3] text-gray-700"
                }`}
              >
                Даты
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("parameters")}
                className={`w-36 h-10 rounded-md flex items-center justify-center font-medium ml-2 ${
                  activeTab === "parameters"
                    ? "bg-[#0832DE] text-white"
                    : "bg-[#E2E7F3] text-gray-700"
                }`}
              >
                Параметры
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("motivation")}
                className={`w-36 h-10 rounded-md flex items-center justify-center font-medium ml-2 ${
                  activeTab === "motivation"
                    ? "bg-[#0832DE] text-white"
                    : "bg-[#E2E7F3] text-gray-700"
                }`}
              >
                Мотивация
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-grow overflow-auto mb-6 p-1">
            {/* Use div instead of form to prevent automatic submission */}
            <div onSubmit={preventFormSubmission}>{renderTabContent()}</div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between mt-auto">
            <button
              type="button"
              onClick={handleReset}
              className="cursor-pointer"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 16C13.671 16 15 14.669 15 13C15 11.331 13.671 10 12 10C10.329 10 9 11.331 9 13C9 14.669 10.329 16 12 16Z"
                  fill="#101216"
                />
                <path
                  d="M20.817 11.186C20.5814 10.0344 20.1209 8.94048 19.462 7.967C18.8146 7.00928 17.9897 6.18442 17.032 5.537C16.0584 4.87833 14.9646 4.41789 13.813 4.182C13.2081 4.05933 12.5922 3.99901 11.975 4.002V2L8 5L11.975 8V6.002C12.459 6 12.943 6.046 13.41 6.142C14.305 6.32541 15.1552 6.68321 15.912 7.195C16.6584 7.69824 17.3008 8.34063 17.804 9.087C18.5853 10.2422 19.002 11.6054 19 13C18.9998 13.9359 18.8128 14.8623 18.45 15.725C18.2735 16.1405 18.0579 16.5383 17.806 16.913C17.5531 17.2854 17.2659 17.6332 16.948 17.952C15.98 18.9182 14.7511 19.5809 13.412 19.859C12.4807 20.047 11.5213 20.047 10.59 19.859C9.69456 19.6754 8.84404 19.3173 8.087 18.805C7.34148 18.3022 6.6998 17.6605 6.197 16.915C5.41656 15.7585 4.9997 14.3952 5 13H3C3.00106 14.7937 3.53689 16.5463 4.539 18.034C5.18685 18.9901 6.01086 19.8142 6.967 20.462C8.45262 21.4675 10.2061 22.0033 12 22C12.6093 21.9999 13.217 21.9386 13.814 21.817C14.9647 21.5794 16.0579 21.1191 17.032 20.462C17.5103 20.1397 17.956 19.7717 18.363 19.363C18.7705 18.9544 19.1388 18.5084 19.463 18.031C20.4676 16.5458 21.0031 14.7931 21 13C20.9999 12.3907 20.9386 11.783 20.817 11.186Z"
                  fill="#101216"
                />
              </svg>
            </button>

            <button
              type="button"
              className="px-6 py-2 text-white rounded-md"
              onClick={handleSubmit}
              style={{ backgroundColor: "#0832DE" }}
            >
              {submitButtonText}
            </button>
          </div>
        </div>

        {/* Close Button */}
        <button
          type="button"
          className="absolute top-2 right-2 p-2 text-white hover:text-gray-200"
          onClick={onClose}
        >
          <X size={24} />
        </button>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default RecruitModalBase;
